import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldCheck,
  LockKeyhole,
  Cloud,
  UserRound,
  Mail,
  Eye,
  EyeOff,
  LogIn,
} from "lucide-react";
import alptekinOfisLogo from "@/assets/alptekin-ofis-logo-transparent.png";
import loginOfficeBg from "@/assets/login-office-bg.png";

const NAVY = "#0B2344";
const NAVY_DARK = "#071A33";
const GOLD = "#C79532";
const CREAM = "#F8F5EF";

const TRUST_ITEMS = [
  { icon: ShieldCheck, title: "Güvenli Erişim", desc: "Şifreli veri saklama" },
  { icon: LockKeyhole, title: "Rol Bazlı Yetkilendirme", desc: "Güvenli kullanıcı yönetimi" },
  { icon: Cloud, title: "Otomatik Yedekleme", desc: "Düzenli veri yedeği" },
];

function formatErr(d) {
  if (!d) return "Bir hata oluştu";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
}

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("smmmsuatalptekin@gmail.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success("Giriş başarılı");
      nav("/");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden flex bg-white">

      {/* SOL KURUMSAL ALAN */}
      <div className="hidden lg:flex relative overflow-hidden w-[55%] flex-col" style={{ backgroundColor: CREAM, color: NAVY }}>

        {/* Dekoratif arka plan */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <img
            src={loginOfficeBg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ backgroundColor: `${CREAM}66` }} />
          <div
            className="absolute inset-0"
            style={{ background: `radial-gradient(ellipse at center, ${CREAM}cc 35%, transparent 68%)` }}
          />
        </div>

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-14 xl:px-20 py-9 min-h-0 text-center">

          {/* LOGO */}
          <div className="shrink-0 mb-8">
            <img
              src={alptekinOfisLogo}
              alt="Alptekin Ofis"
              className="h-52 xl:h-60 w-auto object-contain"
            />
          </div>

          {/* TANITIM */}
          <div className="max-w-lg shrink-0">
            <div className="font-serif text-2xl xl:text-[26px] font-semibold tracking-tight" style={{ color: GOLD }}>
              ALPTEKİN OFİS
            </div>
            <h1 className="font-serif text-[34px] xl:text-[40px] font-semibold tracking-tight leading-[1.14] mt-1">
              SMMM Ofis Yönetim Sistemi
            </h1>

            <div className="w-14 h-[3px] rounded-full mt-5 mb-5 mx-auto" style={{ backgroundColor: GOLD }} />

            <p className="text-[15px] leading-7 text-[#5E6878]">
              Mükellef, beyanname, e-Defter, cari hesap,
              görev ve son tarih süreçlerinizi tek merkezden yönetin.
            </p>
          </div>

          <div className="shrink-0" />
        </div>

        {/* GÜVEN BANDI */}
        <div className="relative z-10 w-full grid grid-cols-3 gap-6 px-14 xl:px-20 py-6 shrink-0" style={{ backgroundColor: NAVY }}>
          {TRUST_ITEMS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-center gap-2.5 min-w-0">
              <Icon size={22} strokeWidth={1.6} style={{ color: GOLD }} className="shrink-0" />
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-white leading-snug">{title}</div>
                <div className="text-[11px] text-white/60 leading-snug">{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className="relative z-10 px-14 xl:px-20 py-4 shrink-0">
          <div className="flex items-center gap-2 text-[11.5px] text-[#697386]">
            <span>KVKK Uyumlu</span>
            <span className="text-[#10213B]/25">|</span>
            <span>Rol bazlı erişim</span>
            <span className="text-[#10213B]/25">|</span>
            <span>Denetim kaydı</span>
          </div>
          <div className="text-[11px] text-[#8A93A2] mt-1">
            © 2026 SMMM Suat Alptekin. Tüm hakları saklıdır.
          </div>
        </div>
      </div>

      {/* SAĞ GİRİŞ ALANI */}
      <div className="flex-1 flex items-center justify-center px-6 py-8 sm:px-10 overflow-y-auto" style={{ backgroundColor: "#FAFBFC" }}>

        {/* Mobilde küçük marka başlığı */}
        <div className="lg:hidden absolute top-6 left-0 right-0 flex flex-col items-center">
          <img src={alptekinOfisLogo} alt="Alptekin Ofis" className="h-14 w-auto object-contain" />
        </div>

        <div
          className="w-full max-w-[540px] bg-white rounded-[22px] border border-[#EEF0F3] p-9 sm:p-11 fade-in"
          style={{ boxShadow: "0 8px 40px rgba(11,35,68,0.08), 0 2px 10px rgba(11,35,68,0.04)" }}
        >
          <form onSubmit={submit}>

            {/* Kullanıcı ikonu */}
            <div className="flex justify-center mb-6">
              <div
                className="w-[68px] h-[68px] rounded-full bg-[#f4f6f8] flex items-center justify-center border-2"
                style={{ borderColor: GOLD }}
              >
                <UserRound size={32} strokeWidth={1.7} style={{ color: NAVY }} />
              </div>
            </div>

            <div className="text-center mb-8">
              <h2 className="font-head text-[28px] font-semibold tracking-tight" style={{ color: "#101c31" }}>
                Hoş geldiniz
              </h2>
              <p className="text-[14.5px] text-[#718096] mt-2">
                Hesabınıza erişmek için giriş yapın.
              </p>
            </div>

            {/* E-POSTA */}
            <div className="space-y-2 mb-4">
              <Label htmlFor="login-email" className="text-sm font-semibold text-[#172238]">
                E-posta
              </Label>
              <div className="relative">
                <Mail
                  size={18}
                  strokeWidth={1.8}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8b96a8] pointer-events-none"
                />
                <Input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="E-posta adresiniz"
                  className="h-[52px] pl-12 pr-4 rounded-lg border-[#dce1e8] bg-white text-[15px] shadow-none focus-visible:ring-2 focus-visible:ring-[#0B2344]/15 focus-visible:border-[#0B2344]"
                />
              </div>
            </div>

            {/* ŞİFRE */}
            <div className="space-y-2">
              <Label htmlFor="login-password" className="text-sm font-semibold text-[#172238]">
                Şifre
              </Label>
              <div className="relative">
                <LockKeyhole
                  size={18}
                  strokeWidth={1.8}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8b96a8] pointer-events-none"
                />
                <Input
                  id="login-password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Şifreniz"
                  className="h-[52px] pl-12 pr-12 rounded-lg border-[#dce1e8] bg-white text-[15px] shadow-none focus-visible:ring-2 focus-visible:ring-[#0B2344]/15 focus-visible:border-[#0B2344]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8b96a8] hover:text-[#172238] transition-colors"
                  aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* HATIRLA / ŞİFRE */}
            <div className="flex items-center justify-between mt-4 mb-6">
              <label className="flex items-center gap-2 text-sm text-[#667085] cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#cbd2dc]"
                  style={{ accentColor: NAVY }}
                />
                Beni hatırla
              </label>
              <button type="button" className="text-sm font-medium text-[#3977e8] hover:underline">
                Şifremi unuttum?
              </button>
            </div>

            {/* GİRİŞ */}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-[52px] rounded-lg text-white font-semibold text-[15px] shadow-none transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2"
              style={{ backgroundColor: NAVY }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = NAVY_DARK; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = NAVY; }}
            >
              {loading ? "Giriş yapılıyor..." : (<><LogIn size={17} /> Giriş yap</>)}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
